/** 后端 DTO → 平台数据结构映射 */

import { cdpEngineAssetUrl } from '@/services/cdpEngineClient';
import { resolveCdpAsset } from '@/utils/platformHelpers';
import { toPlatformStoreId } from '@/utils/materialApiContext';
import { isImageAssetUrl } from '@/utils/templateThumb';

export function unwrapApiList(payload) {
  if (Array.isArray(payload?.result)) return payload.result;
  const result = unwrapApiResult(payload);
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.records)) return result.records;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.content)) return result.content;
  if (Array.isArray(result?.data)) return result.data;
  return [];
}

export function unwrapApiResult(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.success === false) {
    throw new Error(payload.errorDesc || payload.msg || payload.message || '请求失败');
  }
  if ('result' in payload) return payload.result;
  if ('data' in payload) return payload.data;
  return payload;
}

export function unwrapApiTotalElements(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const result = unwrapApiResult(payload);
  const total = result?.totalElements ?? payload?.totalElements ?? result?.total ?? payload?.total ?? result?.totalCount ?? payload?.totalCount;
  return total != null && !Number.isNaN(Number(total)) ? Number(total) : null;
}

function unwrapQueryRows(payload) {
  const result = unwrapApiResult(payload);
  let rows = unwrapApiList(payload);
  if (!rows.length && result && typeof result === 'object' && !Array.isArray(result)) {
    rows = [result];
  }
  return rows;
}

function flattenInfoItems(row, infoKey) {
  const info = row?.[infoKey];
  if (Array.isArray(info)) {
    if (!info.length) return [{ ...row, [infoKey]: undefined }];
    return info.map(item => ({ ...row, ...item, [infoKey]: undefined }));
  }
  if (info && typeof info === 'object') {
    return [{ ...row, ...info, [infoKey]: undefined }];
  }
  return [row];
}

function explicitFolderType(row, folderTypeKey) {
  const type = Number(row?.[folderTypeKey]);
  return type === 1 || type === 2 ? type : null;
}

/**
 * queryMaterial / queryTemplate 可能在同一 result 中混入文件夹(type=1)与文件(type=2)。
 * type=1 即使 materialInfo/templateInfo 为空也要保留为文件夹，type=2 不得渲染成目录。
 */
export function normalizeLibraryFolderRows(payload, infoKey, folderTypeKey) {
  return unwrapQueryRows(payload).flatMap((row) => {
    const type = explicitFolderType(row, folderTypeKey);
    if (type === 2) return [];
    if (type === 1) {
      const folderRow = { ...row };
      delete folderRow[infoKey];
      return [folderRow];
    }
    return flattenInfoItems(row, infoKey);
  });
}

export function normalizeLibraryFileRows(payload, infoKey, folderTypeKey) {
  return unwrapQueryRows(payload).flatMap((row) => {
    const type = explicitFolderType(row, folderTypeKey);
    if (type === 1) return [];
    return flattenInfoItems(row, infoKey);
  });
}

/** 创建文件夹等接口 result 可能是 id 或 { id / folderId } */
export function normalizeRemoteCreatedId(result) {
  if (result == null || result === '') return null;
  if (typeof result === 'object') {
    const id = result.id ?? result.folderId;
    return id == null || id === '' ? null : String(id);
  }
  return String(result);
}

/** 后端 folderId=0 表示根目录，前端统一为 undefined */
export function normalizeMaterialFolderId(folderId) {
  if (folderId == null || folderId === '' || folderId === 0 || folderId === '0') {
    return undefined;
  }
  return String(folderId);
}

export function materialFolderIdsEqual(a, b) {
  return normalizeMaterialFolderId(a) === normalizeMaterialFolderId(b);
}

export function normalizeTemplateFolderId(folderId) {
  return normalizeMaterialFolderId(folderId);
}

export function mapTemplateFolderDtoToPlatform(dto = {}) {
  const name = (dto.folderName != null && String(dto.folderName).trim() !== '')
    ? String(dto.folderName).trim()
    : (dto.name != null && String(dto.name).trim() !== '' ? String(dto.name).trim() : '文件夹');
  return {
    id: String(dto.id ?? ''),
    name,
    parentId: dto.parentId != null && dto.parentId !== 0 ? String(dto.parentId) : undefined,
    templateCount: dto.templateCount ?? 0,
    sort: dto.sort,
    createTime: dto.createTime || '',
  };
}

function resolveFallbackStoreId(fallbackStoreId = 's1') {
  if (!fallbackStoreId || fallbackStoreId === 'all') return 'all';
  return toPlatformStoreId(fallbackStoreId);
}

/** 后端 /api/art/template/use 会另存店铺记录，名称固定加「_副本」 */
export function isSpawnedTemplateCopyName(name) {
  return false;
}

export function resolveTemplateStoreId(dto = {}, fallbackStoreId = 's1') {
  const shopId = dto.shopId != null ? Number(dto.shopId) : null;
  if (Number.isFinite(shopId) && shopId > 0) {
    return toPlatformStoreId(shopId);
  }
  const departmentId = dto.departmentId != null ? Number(dto.departmentId) : null;
  if (Number.isFinite(departmentId) && departmentId > 0) {
    return toPlatformStoreId(departmentId);
  }
  return resolveFallbackStoreId(fallbackStoreId);
}

export function mapTemplateDtoToPlatform(dto = {}, storeId = 's1') {
  const isMaster = Number(dto.isMasterTemplate) === 1
    || dto.templateType === 'master'
    || dto.templateType === 1;
  const name = (dto.folderName != null && String(dto.folderName).trim() !== '')
    ? String(dto.folderName).trim()
    : ((dto.name != null && String(dto.name).trim() !== '')
      ? String(dto.name).trim()
      : ((dto.templateName != null && String(dto.templateName).trim() !== '')
        ? String(dto.templateName).trim()
        : '未命名模版'));

  const coverThumbUrl = dto.coverThumbUrl || dto.thumbUrl || dto.thumb || '';
  return {
    id: String(dto.id ?? dto.templateId ?? ''),
    storeId: resolveTemplateStoreId(dto, storeId),
    name,
    type: isMaster ? 'master' : 'store',
    dieCutType: dto.dieCutType || dto.productCategory || (typeof dto.templateType === 'string' ? dto.templateType : 'bag'),
    widthMm: dto.widthMm ?? dto.width,
    heightMm: dto.heightMm ?? dto.height,
    bleedMm: dto.bleedMm ?? dto.bleed,
    coverThumbUrl,
    thumb: coverThumbUrl
      || (isImageAssetUrl(dto.originFileUrl) ? dto.originFileUrl : ''),
    originFileUrl: dto.originFileUrl || dto.fileUrl || dto.pdfUrl || '',
    folderId: normalizeTemplateFolderId(dto.folderId),
    tags: dto.tags || [],
    layerJson: dto.layerJson || dto.layersJson || dto.layerContentJson || dto.pageLayerJson || dto.layerData || dto.content || dto.data || '',
    templateLayerId: String(dto.templateLayerId ?? dto.trackId ?? '').trim(),
    pageCount: dto.pageTotal ?? dto.pageCount ?? 1,
    useCount: dto.useCount ?? 0,
    uploaded: (dto.createTime || dto.updateTime || dto.updatedTime || '').slice(0, 10),
    updated: dto.updateTime || dto.updatedTime || dto.createTime || '',
  };
}

export function mapTemplatePageLayerDto(dto = {}) {
  return {
    id: String(dto.id ?? ''),
    templateId: dto.templateId != null ? String(dto.templateId) : undefined,
    pageIndex: dto.pageIndex ?? 0,
    pageName: dto.pageName || '',
    thumb: dto.pageThumbUrl || '',
    layerJson: dto.pageLayerJson || dto.layerContentJson || dto.layerJson || dto.contentJson || '',
    syncRelationJson: dto.syncRelationJson || '',
    bagAreaJson: dto.bagAreaJson || '',
  };
}

export function mapPlatformTemplateToImportPayload(tpl = {}) {
  const layerJson = typeof tpl.layerJson === 'string'
    ? tpl.layerJson.trim()
    : (tpl.layerJson ? JSON.stringify(tpl.layerJson) : '');

  const departmentId = tpl.departmentId != null
    ? Number(tpl.departmentId)
    : undefined;

  const coverThumbUrl = tpl.coverThumbUrl || (
    tpl.thumb && /^https?:\/\//i.test(String(tpl.thumb).trim()) ? String(tpl.thumb).trim() : ''
  );
  const templateLayerId = String(tpl.templateLayerId ?? tpl.trackId ?? '').trim();

  return {
    coverThumbUrl: coverThumbUrl || '',
    departmentId,
    departmentName: tpl.departmentName || undefined,
    folderId: normalizeTemplateFolderId(tpl.folderId) ? Number(normalizeTemplateFolderId(tpl.folderId)) : 0,
    layerJson,
    originFileUrl: tpl.originFileUrl || '',
    templateName: tpl.name || tpl.templateName || '',
    templateType: tpl.type === 'master' ? 'master' : (tpl.templateType || tpl.dieCutType || 'bag'),
    ...(templateLayerId ? { templateLayerId } : {}),
    ...(tpl.type === 'master' ? { isMasterTemplate: 1 } : {}),
  };
}

export function isLayerJsonAssetRef(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed.startsWith('{') || trimmed.startsWith('[')) return false;
  return /^https?:\/\//i.test(trimmed)
    || trimmed.startsWith('/')
    || trimmed.includes('uploads/')
    || /\.json(\?|$)/i.test(trimmed);
}

/** 将 Python 分层结果 / 本地路径 / 对象规范为 import 接口 layerJson 字符串 */
export function normalizeInlineTemplateLayerJson(value) {
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      throw new Error('模板分层数据 layerJson 解析失败');
    }
  }

  let current = String(value || '').trim();
  let decodedString = false;
  for (let depth = 0; depth < 4; depth += 1) {
    if (!current || (!current.startsWith('{') && !current.startsWith('[') && !current.startsWith('"'))) {
      throw new Error('模板分层数据 layerJson 不是有效的 JSON');
    }
    let parsed;
    try {
      parsed = JSON.parse(current);
    } catch {
      throw new Error('模板分层数据 layerJson 解析失败');
    }
    if (parsed && typeof parsed === 'object') {
      return decodedString ? JSON.stringify(parsed) : current;
    }
    if (typeof parsed !== 'string') {
      throw new Error('模板分层数据 layerJson 不是有效的 JSON');
    }
    current = parsed.trim();
    decodedString = true;
  }
  throw new Error('模板分层数据 layerJson 解析层级过多');
}

export async function normalizeTemplateLayerJson(layerJson) {
  if (layerJson == null || layerJson === '') return '';
  if (typeof layerJson === 'object') return normalizeInlineTemplateLayerJson(layerJson);
  const trimmed = String(layerJson).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')) {
    return normalizeInlineTemplateLayerJson(trimmed);
  }

  if (isLayerJsonAssetRef(trimmed)) {
    const fetchUrl = cdpEngineAssetUrl(trimmed) || resolveCdpAsset(trimmed);
    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error(`读取模板图层失败: ${response.status}`);
    return normalizeInlineTemplateLayerJson(await response.text());
  }

  throw new Error('模板分层数据 layerJson 不是有效的 JSON');
}

export function mapMaterialFolderDtoToPlatform(dto = {}) {
  const name = (dto.folderName != null && String(dto.folderName).trim() !== '')
    ? String(dto.folderName).trim()
    : (dto.name != null && String(dto.name).trim() !== '' ? String(dto.name).trim() : '文件夹');
  return {
    id: String(dto.id ?? ''),
    name,
    parentId: dto.parentId != null ? String(dto.parentId) : undefined,
    materialCount: dto.materialCount ?? 0,
    sort: dto.sort,
    createTime: dto.createTime || '',
  };
}

export function resolveMaterialStoreId(dto = {}, fallbackStoreId = 's1') {
  const shopId = dto.shopId != null ? Number(dto.shopId) : null;
  if (Number.isFinite(shopId) && shopId > 0) {
    return toPlatformStoreId(shopId);
  }
  const departmentId = dto.departmentId != null ? Number(dto.departmentId) : null;
  if (Number.isFinite(departmentId) && departmentId > 0) {
    return toPlatformStoreId(departmentId);
  }
  return resolveFallbackStoreId(fallbackStoreId);
}

export function mapMaterialDtoToPlatform(dto = {}, storeId = 's1') {
  const resolvedStoreId = resolveMaterialStoreId(dto, storeId);
  const departmentId = dto.departmentId != null ? Number(dto.departmentId) : undefined;
  const name = (dto.folderName != null && String(dto.folderName).trim() !== '')
    ? String(dto.folderName).trim()
    : ((dto.name != null && String(dto.name).trim() !== '')
      ? String(dto.name).trim()
      : ((dto.materialName != null && String(dto.materialName).trim() !== '')
        ? String(dto.materialName).trim()
        : '未命名素材'));

  return {
    id: String(dto.id ?? ''),
    storeId: resolvedStoreId,
    departmentId: Number.isFinite(departmentId) ? departmentId : undefined,
    scope: departmentId === 0 ? 'platform' : 'store',
    name,
    category: dto.materialType || dto.category || 'graphic',
    url: dto.originUrl || dto.url || '',
    thumb: dto.thumbUrl || dto.thumb || dto.originUrl || '',
    folderId: normalizeMaterialFolderId(dto.folderId),
    uploadedAt: dto.createTime || dto.updateTime || '',
    uploadUserId: dto.uploadUserId ?? dto.createUserId ?? null,
    createUserId: dto.createUserId ?? dto.uploadUserId ?? null,
    createUserName: dto.createUserName || '',
  };
}

export function mapPaletteColorDtoToPlatform(dto = {}, paletteId = '') {
  return {
    id: String(dto.id ?? `${paletteId}_${dto.colorName || 'color'}`),
    paletteId: dto.paletteId != null ? String(dto.paletteId) : (paletteId ? String(paletteId) : undefined),
    name: dto.colorName || dto.name || '色板',
    c: Number(dto.c ?? dto.cmykC ?? 0),
    m: Number(dto.m ?? dto.cmykM ?? 0),
    y: Number(dto.y ?? dto.cmykY ?? 0),
    k: Number(dto.k ?? dto.cmykK ?? 0),
    sort: dto.sort,
    previewUrl: dto.colorPreviewUrl || '',
  };
}

export function mapPlatformColorToSavePayload(color = {}, paletteId) {
  // color/add 的 id 不会更新已有色卡（会新建），默认不传 id
  const resolvedPaletteId = paletteId ?? color.paletteId;
  const body = {
    paletteId: Number(resolvedPaletteId),
    colorName: color.name || color.colorName || '色板',
    c: Number(color.c ?? 0),
    m: Number(color.m ?? 0),
    y: Number(color.y ?? 0),
    k: Number(color.k ?? 0),
    sort: color.sort ?? 0,
  };
  if (!Number.isFinite(body.paletteId)) {
    throw new Error('缺少色板分组 ID（paletteId）');
  }
  if (color.id != null && color.id !== '' && !String(color.id).startsWith('c_') && color.forceId) {
    body.id = Number(color.id) || color.id;
  }
  return body;
}

export function mapDraftDtoToPlatform(dto = {}, shopIdFallback) {
  const draftStatus = dto.draftStatus != null ? Number(dto.draftStatus) : null;
  let status = dto.status || dto.designStatus;
  if (!status) {
    status = draftStatus === 1 ? 'done' : 'draft';
  }
  const shopRaw = dto.shopId != null ? dto.shopId : shopIdFallback;
  return {
    id: String(dto.id ?? ''),
    name: dto.designName || dto.name || '未命名稿件',
    designName: dto.designName || dto.name || '未命名稿件',
    designType: dto.designType ?? 0,
    draftStatus,
    taskId: dto.taskId != null ? String(dto.taskId) : undefined,
    templateId: dto.templateId != null ? String(dto.templateId) : undefined,
    templateName: dto.templateName || '',
    orderId: dto.orderNo || dto.orderId || '',
    songName: dto.songName || '',
    storeId: shopRaw != null ? `s${String(shopRaw).replace(/^s/i, '')}` : (dto.storeId || 's1'),
    status,
    thumb: dto.coverUrl || dto.thumbUrl || dto.thumb || '',
    totalPage: dto.totalPage ?? 0,
    updated: dto.updateTime || dto.updatedTime || dto.createTime || '',
    created: dto.createTime || '',
    standalone: !dto.taskId,
  };
}

export function mapDraftPageLayerDto(dto = {}) {
  return {
    id: String(dto.id ?? ''),
    draftId: dto.draftId != null ? String(dto.draftId) : undefined,
    pageIndex: dto.pageIndex ?? 0,
    pageName: dto.pageName || '',
    layerContentJson: dto.layerContentJson || '',
    groupConfigJson: dto.groupConfigJson || '',
    bagPullJson: dto.bagPullJson || '',
    syncConfigJson: dto.syncConfigJson || '',
    pagePreviewUrl: dto.pagePreviewUrl || '',
  };
}

export function mapBatchGroupDto(dto = {}) {
  return {
    id: String(dto.id ?? ''),
    draftId: dto.draftId != null ? String(dto.draftId) : undefined,
    groupName: dto.groupName || '',
    groupElementIds: dto.groupElementIds || '',
  };
}

export function mapConfigDtoToRecord(dto = {}) {
  return {
    key: dto.configKey,
    value: dto.configValue,
    desc: dto.configDesc,
    id: dto.id,
  };
}

export function mapPlatformTemplateToSavePayload(tpl = {}) {
  return {
    id: tpl.id && !String(tpl.id).startsWith('t_') ? Number(tpl.id) || tpl.id : undefined,
    templateName: tpl.name,
    shopId: tpl.storeId?.replace(/^s/, '') || undefined,
    folderId: tpl.folderId ? Number(tpl.folderId) : undefined,
    thumbUrl: tpl.thumb,
    layerJson: tpl.layerJson,
  };
}

export function mapPlatformMaterialToUploadPayload(material = {}, fileMeta = {}, department = {}) {
  const payload = {
    folderId: Number(normalizeMaterialFolderId(material.folderId) || 0),
    materialName: material.name || material.materialName,
    materialType: material.category || material.materialType || 'upload',
    originUrl: material.url || material.originUrl || '',
    thumbUrl: material.thumb || material.thumbUrl || material.url || '',
    fileExt: fileMeta.fileExt || material.fileExt || '',
    fileSize: fileMeta.fileSize || material.fileSize || 0,
    width: fileMeta.width || material.width || 0,
    height: fileMeta.height || material.height || 0,
  };
  if (department.departmentId != null) {
    payload.departmentId = Number(department.departmentId) || department.departmentId;
  }
  if (department.departmentName) {
    payload.departmentName = department.departmentName;
  }
  return payload;
}

export function mapPlatformDraftToSavePayload(draft = {}) {
  return {
    designName: draft.name || draft.designName,
    designType: draft.designType ?? (draft.taskId ? 1 : 0),
    taskId: draft.taskId ? Number(draft.taskId) : undefined,
    templateId: draft.templateId && !String(draft.templateId).startsWith('t_')
      ? Number(draft.templateId)
      : undefined,
    shopId: draft.storeId?.replace(/^s/i, '') || undefined,
  };
}

/** 设计中心模版类型：1 已保存，2 草稿 */
export const DESIGN_CENTER_TEMPLATE_TYPE = {
  SAVED: 1,
  DRAFT: 2,
};

/** 设计中心操作类型：1 草稿/实时保存，2 正式保存，3 修改母模版 */
export const DESIGN_CENTER_OP_TYPE = {
  DRAFT: 1,
  SAVE: 2,
  UPDATE_TEMPLATE: 3,
};

function parseLayerJsonState(layerJson) {
  if (layerJson == null || layerJson === '') return { layerJson: '', state: undefined };
  if (typeof layerJson === 'object') {
    return { layerJson: JSON.stringify(layerJson), state: layerJson };
  }
  try {
    return { layerJson: String(layerJson), state: JSON.parse(layerJson) };
  } catch {
    return { layerJson: String(layerJson), state: undefined };
  }
}

export function mapDesignCenterTemplateDtoToPlatform(dto = {}, storeIdFallback = 's1') {
  const templateType = dto.templateType != null ? Number(dto.templateType) : null;
  const storeId = dto.departmentId != null
    ? `s${String(dto.departmentId).replace(/^s/i, '')}`
    : storeIdFallback;
  const { layerJson, state } = parseLayerJsonState(dto.layerJson);
  const isSaved = templateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED;

  return {
    id: String(dto.id ?? ''),
    designName: dto.designName || '未命名设计',
    templateName: dto.templateName || dto.designName || '未命名设计',
    templateId: dto.templateId != null ? String(dto.templateId) : undefined,
    originFileUrl: dto.originFileUrl || '',
    coverThumbUrl: dto.coverThumbUrl || '',
    layerJson,
    templateType,
    standalone: true,
    // 设计中心接口已经按 departmentId 做过数据权限与店铺隔离。
    // 不再用后端 createUserName 与前端演示账号姓名做二次归属判断，
    // 否则保存成功的记录会因为两套账号姓名不一致而被页面隐藏。
    remoteDesignCenter: true,
    storeId,
    status: isSaved ? 'done' : 'draft',
    thumb: dto.coverThumbUrl || dto.originFileUrl || '',
    updated: dto.updateTime || dto.createTime || '',
    created: dto.createTime || '',
    createUserId: dto.createUserId,
    createUserName: dto.createUserName || '',
    ownerName: dto.createUserName || '',
    state,
  };
}

/**
 * 保存接口必须带修改后的全量分层：优先当前画布 state，禁止用缓存原稿 / 局部 diff。
 */
export function resolveFullEditorLayerSource(design = {}) {
  if (design.state != null && design.state !== '') return design.state;
  if (design.layerJson != null && design.layerJson !== '') return design.layerJson;
  return '';
}

export function toFullEditorLayerJson(source) {
  if (source == null || source === '') return '';
  let doc = source;
  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) return '';
    try {
      doc = JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }
  if (typeof doc !== 'object' || doc === null) {
    return typeof source === 'string' ? source : JSON.stringify(source);
  }
  let next;
  try {
    next = JSON.parse(JSON.stringify(doc));
  } catch {
    return JSON.stringify(doc);
  }
  if (next.meta && typeof next.meta === 'object') {
    const meta = { ...next.meta };
    delete meta.layerJson;
    delete meta.layersJson;
    delete meta.mockupLayersJson;
    delete meta.extraDesignPages;
    delete meta.savedState;
    delete meta._layerJsonOmitted;
    next.meta = meta;
  }
  if (Array.isArray(next.pages) && next.pages.length) {
    const currentIndex = Number.isInteger(next.currentPageIndex) ? next.currentPageIndex : 0;
    const currentPage = next.pages[currentIndex] || next.pages[0];
    if (currentPage && Array.isArray(currentPage.objects)) {
      next.objects = currentPage.objects;
    }
  }
  return JSON.stringify(next);
}

export function mapPlatformDesignCenterToRequestPayload(design = {}, templateType) {
  const layerJson = toFullEditorLayerJson(resolveFullEditorLayerSource(design)) || undefined;
  const remoteId = design.id && !String(design.id).startsWith('sd_') && !String(design.id).startsWith('d_')
    ? Number(design.id) || design.id
    : undefined;

  const resolvedTemplateType = templateType ?? design.templateType;
  let templateId = undefined;
  if (design.templateId != null && design.templateId !== '') {
    const parsed = Number(String(design.templateId).replace(/\D/g, ''));
    if (!Number.isNaN(parsed)) templateId = parsed;
  }
  if (templateId === undefined) {
    templateId = 0;
  }

  const payload = {
    designName: design.designName || design.name || '未命名设计',
    templateId,
    templateName: design.templateName || design.designName || '未命名设计',
    originFileUrl: design.originFileUrl || design.thumb || '',
    layerJson,
    templateType: resolvedTemplateType,
    createUserId: design.createUserId != null ? Number(design.createUserId) : undefined,
    opType: design.opType ?? (
      resolvedTemplateType === DESIGN_CENTER_TEMPLATE_TYPE.SAVED
        ? DESIGN_CENTER_OP_TYPE.SAVE
        : DESIGN_CENTER_OP_TYPE.DRAFT
    ),
  };
  const coverThumbUrl = String(design.coverThumbUrl || '').trim();
  if (/^https?:\/\//i.test(coverThumbUrl)) payload.coverThumbUrl = coverThumbUrl;
  if (remoteId != null) payload.id = remoteId;
  return payload;
}
